import React, { useEffect, useMemo, useState } from 'react';
import { useGqlMutation } from '@API/useAvQuery';
import { AvTableV2 } from '@components/AvTableController';
import {
  AttentionBoxVariant,
  AvAttentionBox,
  AvTooltip,
  CodeEditor,
  ColumnReorder,
  TableSizeEnum,
  TableV2PaginationType,
  TColumnsDef,
  TextInput,
} from '@fabric-ui/components';
import { flex } from '@fabric-ui/theme';
import { ellipsis, getIfValidStringifiedObject, setNestedValue } from '@fabric-ui/utils';
import { Box, Button, Divider, Skeleton, Typography } from '@mui/material';
import { getNetworkErrorCode } from '@utils/utils';
import { gql } from 'graphql-request';
import { useSnackbar } from 'notistack';
import { useParams } from '../../../utils/AvRouter';
import { useMappingContext } from './MappingContext';
import { PREVIEW_PAGE_SIZE } from './types';
import { extractPropertiesFromScript, getHeadCells } from './utils';

const EXECUTE_SCRIPT_QUERY = gql`
  mutation executePythonScript($script: String!, $jsonData: String!) {
    executePythonScript(script: $script, jsonData: $jsonData)
  }
`;

const ScriptEditorTest = ({
  value: mappingItemEditorValue,
  onChange: onChangeMappingItem,
}: {
  value?: string;
  onChange?: (value: string) => void;
}) => {
  const { previewRows, rawParsedFields, workflowId, isLoadingPreviewMappingQuery, fieldTypesMap } = useMappingContext();
  const { id: instanceId } = useParams();

  const [fieldInScriptUserValue, setFieldInScriptUserValue] = useState({});
  const [testResult, setTestResult] = useState<any>(undefined);
  const [activeRowId, setActiveRowId] = useState<string | undefined>();
  const { enqueueSnackbar } = useSnackbar();

  const { mutateAsync: runTest, isPending: isTesting } = useGqlMutation({
    mutation: EXECUTE_SCRIPT_QUERY,
    onSuccess: result => {
      setTestResult(JSON.stringify(result));
    },
    onError: error => {
      if (getNetworkErrorCode(error) === 400) {
        enqueueSnackbar(error.message, { variant: 'error' });
      } else {
        throw error;
      }
    },
  });

  const fieldsInScript = useMemo(
    () => extractPropertiesFromScript(typeof mappingItemEditorValue === 'string' ? mappingItemEditorValue : ''),
    [mappingItemEditorValue]
  );

  const MAX_VISIBLE_COLUMNS = 20;

  const initialHeadCells = useMemo(() => {
    const rawCells = getHeadCells({ headers: rawParsedFields, instanceId, lastWfRun: workflowId, headerTypes: fieldTypesMap });
    const filtered = rawCells.filter(hc => hc.id !== 'avalor_unique_key');
    const visibleCandidates = filtered.filter(hc => !hc.isKey && !hc.hidden).map(hc => hc.id);
    const initialIds = visibleCandidates.slice(0, MAX_VISIBLE_COLUMNS);
    const initiallyVisible = new Set(initialIds);
    return [
      { id: 'rowId', header: '', meta: { isKey: true, hidden: true } },
      ...filtered.map(hc => ({
        id: hc.id,
        header: <span style={{ textTransform: 'none' }}>{hc.label ?? hc.id}</span>,
        cell: hc.formatter
          ? (info: any) => (
              <AvTooltip>{hc.formatter?.(info.getValue(), info.row.original, undefined, info.row.index, undefined, hc.id)}</AvTooltip>
            )
          : undefined,
        meta: {
          ...(hc.type ? { type: hc.type } : {}),
          isKey: hc.isKey,
          hidden: hc.isKey ? false : !!hc.hidden || !initiallyVisible.has(hc.id),
        },
      })),
    ] as TColumnsDef;
  }, [rawParsedFields, instanceId, workflowId, fieldTypesMap]);

  const [tableHeadCells, setTableHeadCells] = useState<TColumnsDef>(initialHeadCells);

  useEffect(() => {
    setTableHeadCells(prev =>
      prev.length !== initialHeadCells.length || prev.some((c, i) => c.id !== initialHeadCells[i].id) ? initialHeadCells : prev
    );
  }, [initialHeadCells]);

  const rowsWithKeys = useMemo(() => previewRows?.map(row => ({ ...row, rowId: crypto.randomUUID() })) || [], [previewRows]);

  const visibleColumns = useMemo(() => tableHeadCells.filter(c => !c.meta?.hidden || c.meta?.isKey), [tableHeadCells]);

  const setFieldDataFromTableRow = (rowId: string | undefined) => {
    if (!rowId) return;

    const selectedRow = rowsWithKeys.find(row => row.rowId === rowId);
    if (!selectedRow) return;

    setFieldInScriptUserValue(
      fieldsInScript.reduce((acc, field) => {
        if (selectedRow[field] !== undefined) {
          return { ...acc, [field]: selectedRow[field] };
        }

        const nestedKeys = Object.keys(selectedRow).filter(key => key.startsWith(`${field}.`));

        if (nestedKeys.length > 0) {
          const subObject = nestedKeys.reduce((currentSubObject, nestedKey) => {
            const path = nestedKey.substring(field.length + 1);
            return setNestedValue(currentSubObject, path, selectedRow[nestedKey]);
          }, {} as any);

          return { ...acc, [field]: subObject };
        }
        return { ...acc, [field]: selectedRow[field] };
      }, {})
    );
  };

  useEffect(() => {
    setFieldDataFromTableRow(activeRowId);
  }, [activeRowId]);

  const handleRunTest = () => {
    setTestResult(undefined);
    const jsonData = JSON.stringify(fieldInScriptUserValue);
    runTest({ script: mappingItemEditorValue, jsonData });
  };
  return (
    <Box sx={{ ...flex.row, width: '100%', height: '88%', flexBasis: '40%', gap: 2 }}>
      <CodeEditor value={mappingItemEditorValue as string} onChange={onChangeMappingItem} height="100%" />
      <Box
        sx={{
          flexBasis: '60%',
          ...flex.col,
          width: '60%',
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'var(--color-border-interactive-secondary-default)',
          flexShrink: 0,
        }}>
        <Box sx={{ minHeight: 200, maxHeight: 300, px: 3, pt: 2, overflow: 'auto' }}>
          <Typography variant="h6">Test Logic</Typography>
          <div>Manually populate input fields or select a record from the table</div>
          <Box sx={{ ...flex.row, pt: 1, flexWrap: 'wrap', gap: 2 }}>
            {fieldsInScript.length ? (
              fieldsInScript.map(f => (
                <TextInput
                  key={f}
                  label={f || ' '}
                  size="small"
                  sx={{ width: 200 }}
                  onChange={v => {
                    setFieldInScriptUserValue(prev => ({ ...prev, [f]: getIfValidStringifiedObject(v, true) || v }));
                  }}
                  value={
                    typeof fieldInScriptUserValue[f] === 'object' ? JSON.stringify(fieldInScriptUserValue[f]) : fieldInScriptUserValue[f]
                  }
                />
              ))
            ) : (
              <AvAttentionBox
                sx={{ width: '100%', textAlign: 'center', pt: 1.5 }}
                showIcon={false}
                variant={AttentionBoxVariant.Neutral}
                content="Edit script to expose input fields for logic test"
              />
            )}
          </Box>
        </Box>
        <Box sx={{ ...flex.col, maxHeight: '100%', overflow: 'auto' }}>
          <Box sx={{ ...flex.row, alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
            <Typography variant="h6" sx={{ py: 2, pl: 3 }}>
              Select a record to populate input fields and test logic
            </Typography>
            <ColumnReorder items={tableHeadCells} setItems={setTableHeadCells} initialColumns={initialHeadCells} />
          </Box>
          <AvTableV2
            resizable
            size={TableSizeEnum.xSmall}
            headCells={visibleColumns}
            enableActiveRowHighlighting
            rows={rowsWithKeys}
            loading={isLoadingPreviewMappingQuery}
            activeRowId={activeRowId}
            onActiveRowIdChange={fieldsInScript.length ? setActiveRowId : undefined}
            pagination={{
              type: TableV2PaginationType.CLIENT_NOT_CONTROLLED,
              rowsPerPage: PREVIEW_PAGE_SIZE,
            }}
            sx={{ pl: 3 }}
          />
          <Divider />
          <Box sx={{ ...flex.row, alignItems: 'center', p: 2, gap: 2, flex: 1, minHeight: 70 }}>
            {testResult !== undefined && (
              <Box sx={{ ...ellipsis, borderRadius: 1, background: 'var(--color-surface-status-success-primary)', py: 1, px: 2 }}>
                <AvTooltip>{testResult}</AvTooltip>
              </Box>
            )}
            {isTesting && <Skeleton sx={{ height: 37, width: 150 }} />}
            <span style={{ flex: 1 }} />
            <Button disabled={isTesting} sx={{ flexShrink: 0, justifySelf: 'end' }} variant="outlined" size="small" onClick={handleRunTest}>
              Run Test
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ScriptEditorTest;
